// The longest side (px) of a PNG. Browsers refuse a larger canvas (or draw
// it blank); a larger image is drawn at a smaller scale instead.
export const MAX_CANVAS_SIDE = 16384;

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("The image could not be drawn"));
    image.src = url;
  });

/**
 * Draws an SVG, given as the data URL toSvg makes, onto a canvas over
 * `background` and returns it as a PNG at `pixelRatio` device pixels per CSS
 * pixel. It must be a data URL: Chrome taints a canvas drawn from an object
 * URL of an SVG with HTML in it, and then makes no PNG of it.
 */
export const pngOfSvg = async (
  svgUrl: string,
  background: string,
  pixelRatio: number,
): Promise<Blob> => {
  const image = await loadImage(svgUrl);
  // Some browsers draw an SVG's foreign content only once it is decoded;
  // one that cannot say so is drawn as it is
  await image.decode?.().catch(() => undefined);
  const width = image.naturalWidth || image.width;
  const height = image.naturalHeight || image.height;
  if (!width || !height) {
    throw new Error("The image has no size");
  }
  const scale = Math.min(pixelRatio, MAX_CANVAS_SIDE / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("The image could not be drawn");
  }
  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  const png = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/png"),
  );
  if (!png) {
    throw new Error("The image is too large to save as a PNG");
  }
  return png;
};
